// import React, { useState, useEffect, useCallback, useMemo } from "react";
// import { useNavigate } from "react-router-dom";
// import { toast } from "react-toastify";
// import { 
//   Users, AlertCircle, CheckCircle, Award, 
//   Search, Download, Network, ChevronLeft, ChevronRight, Filter
// } from "lucide-react";
// import api from "../../common/apiClient";
// import SummaryApi from "../../common/SummaryApi";
// import PageLoader from "../../helpers/PageLoader";
// import { useSelector } from "react-redux";
// import SelectDropdown from "../../customStyles/SelectDropdown";

// export default function TeamReviews() {
//   const navigate = useNavigate();
//   const currentUser = useSelector((state) => state?.user?.user);

//   const [loading, setLoading] = useState(true);
//   const [teamReviews, setTeamReviews] = useState([]);
  
//   // Filter States
//   const [searchQuery, setSearchQuery] = useState("");
//   const [selectedDept, setSelectedDept] = useState("");
//   const [selectedDesig, setSelectedDesig] = useState("");
//   const [selectedDiv, setSelectedDiv] = useState(""); // ✅ Added Division filter state
//   const [selectedStatus, setSelectedStatus] = useState("");
//   const [currentPage, setCurrentPage] = useState(1);
//   const itemsPerPage = 10;

//   const loadTeamReviews = useCallback(async () => {
//     setLoading(true);
//     try {
//       const url = SummaryApi.getReporteesReviews?.url || "/api/hr/kra/reviews/reportees";
//       const res = await api.get(url);
//       if (res.data.success) {
//         setTeamReviews(res.data.data || []);
//       }
//     } catch {
//       toast.error("Failed to load team members' performance metrics.");
//     } finally {
//       setLoading(false);
//     }
//   }, []);

//   useEffect(() => {
//     loadTeamReviews();
//   }, [loadTeamReviews]);

//   // Reset pagination to first page on filters change
//   useEffect(() => {
//     setCurrentPage(1);
//   }, [searchQuery, selectedDept, selectedDesig, selectedDiv, selectedStatus]);

//   // Dynamic Metadata Lists for Dropdowns
//   const departments = useMemo(() => {
//     const depts = teamReviews.map(r => r.userId?.departmentId?.name).filter(Boolean);
//     return [...new Set(depts)].map(d => ({ label: d, value: d }));
//   }, [teamReviews]);

//   const designations = useMemo(() => {
//     const desigs = teamReviews.map(r => r.userId?.designationId?.title || r.userId?.designationId?.name).filter(Boolean);
//     return [...new Set(desigs)].map(d => ({ label: d, value: d }));
//   }, [teamReviews]);

//   // Dynamic Division options calculation (Added)
//   const divisions = useMemo(() => {
//     const divs = teamReviews.map(r => r.userId?.divisionId?.name).filter(Boolean);
//     return [...new Set(divs)].map(d => ({ label: d, value: d }));
//   }, [teamReviews]);

//   // Dynamic Stats Calculations
//   const stats = useMemo(() => {
//     const totalCount = teamReviews.length;
    
//     const pendingCount = teamReviews.filter(r => {
//       const activeStage = r.stages?.[r.currentStageIndex];
//       const activeEvaluatorId = activeStage?.evaluatorId?._id || activeStage?.evaluatorId;
//       return r.status !== "COMPLETED" && activeEvaluatorId?.toString() === currentUser?._id?.toString();
//     }).length;

//     const completedCount = teamReviews.filter(r => r.status === "COMPLETED").length;

//     const ratings = teamReviews
//       .map(r => {
//         const finalEval = r.overallEvaluations?.[r.overallEvaluations.length - 1];
//         return finalEval?.overallScore || 0;
//       })
//       .filter(score => score > 0);

//     const avgRating = ratings.length > 0 
//       ? (ratings.reduce((sum, val) => sum + val, 0) / ratings.length).toFixed(1) 
//       : "0.0";

//     return { totalCount, pendingCount, completedCount, avgRating };
//   }, [teamReviews, currentUser]);

//   // Front-end filters logic
//   const filteredReviews = useMemo(() => {
//     return teamReviews.filter(r => {
//       const user = r.userId || {};
//       const fullName = `${user.firstName || ""} ${user.lastName || ""}`.toLowerCase();
//       const matchesSearch = fullName.includes(searchQuery.toLowerCase()) || 
//                             (user.userId && user.userId.toLowerCase().includes(searchQuery.toLowerCase()));
      
//       const userDept = user.departmentId?.name || "";
//       const matchesDept = !selectedDept || userDept === selectedDept;

//       const userDesig = user.designationId?.title || user.designationId?.name || "";
//       const matchesDesig = !selectedDesig || userDesig === selectedDesig;

//       // Division matching logic (Added)
//       const userDiv = user.divisionId?.name || "";
//       const matchesDiv = !selectedDiv || userDiv === selectedDiv;

//       const matchesStatus = !selectedStatus || r.status === selectedStatus;

//       return matchesSearch && matchesDept && matchesDesig && matchesDiv && matchesStatus;
//     });
//   }, [teamReviews, searchQuery, selectedDept, selectedDesig, selectedDiv, selectedStatus]);

//   // Front-end Pagination
//   const paginatedReviews = useMemo(() => {
//     const startIdx = (currentPage - 1) * itemsPerPage;
//     return filteredReviews.slice(startIdx, startIdx + itemsPerPage);
//   }, [filteredReviews, currentPage]);

//   const totalPages = Math.ceil(filteredReviews.length / itemsPerPage);

//   const resetAllFilters = () => {
//     setSelectedDept("");
//     setSelectedDesig("");
//     setSelectedDiv("");
//     setSelectedStatus("");
//     setSearchQuery("");
//   };

//   if (loading) return <PageLoader />;

//   return (
//     <div className="p-2 space-y-6 animate-in fade-in duration-300">
      
//       {/* HEADER CONTROLS */}
//       <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
//         <div>
//           <h1 className="text-xl font-bold text-slate-900 tracking-tight text-left">Team Performance Reviews</h1>
//           <p className="text-xs text-slate-400 mt-1 text-left">View and manage your team members and their performance progress.</p>
//         </div>
//         <div className="flex items-center gap-3">
//           <button className="flex items-center gap-1.5 bg-white border border-slate-200 hover:bg-slate-50 text-slate-700 text-xs font-bold px-4 py-2 rounded-lg shadow-sm transition-all">
//             <Network className="w-4 h-4" />
//             <span>Team Structure</span>
//           </button>
//           <button className="flex items-center gap-1.5 bg-white border border-slate-200 hover:bg-slate-50 text-slate-700 text-xs font-bold px-4 py-2 rounded-lg shadow-sm transition-all">
//             <Download className="w-4 h-4" />
//             <span>Export</span>
//           </button>
//         </div>
//       </div>

//       {/* STATS ROW */}
//       <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-6">
        
//         {/* Total Team Members */}
//         <div className="bg-white border rounded-2xl p-5 shadow-sm flex items-center gap-4">
//           <div className="w-12 h-12 bg-blue-50 border border-blue-100 rounded-full flex items-center justify-center text-blue-600 shrink-0">
//             <Users className="w-5 h-5" />
//           </div>
//           <div className="text-left">
//             <span className="block text-[10px] text-slate-400 font-bold uppercase tracking-wider">Total Members</span>
//             <p className="text-2xl font-bold text-slate-900 mt-0.5">{stats.totalCount}</p>
//             <span className="text-[10px] text-slate-400 font-semibold">Active workspace</span>
//           </div>
//         </div>

//         {/* Reviews Pending */}
//         <div className="bg-white border rounded-2xl p-5 shadow-sm flex items-center gap-4">
//           <div className="w-12 h-12 bg-amber-50 border border-amber-100 rounded-full flex items-center justify-center text-amber-600 shrink-0">
//             <AlertCircle className="w-5 h-5" />
//           </div>
//           <div className="text-left">
//             <span className="block text-[10px] text-slate-400 font-bold uppercase tracking-wider">Reviews Pending</span>
//             <p className="text-2xl font-bold text-slate-900 mt-0.5">{stats.pendingCount}</p>
//             <span className="text-[10px] text-red-600 font-semibold">Requires your action</span>
//           </div>
//         </div>

//         {/* Completed Reviews */}
//         <div className="bg-white border rounded-2xl p-5 shadow-sm flex items-center gap-4">
//           <div className="w-12 h-12 bg-indigo-50 border border-indigo-100 rounded-full flex items-center justify-center text-indigo-600 shrink-0">
//             <CheckCircle className="w-5 h-5" />
//           </div>
//           <div className="text-left">
//             <span className="block text-[10px] text-slate-400 font-bold uppercase tracking-wider">Completed Reviews</span>
//             <p className="text-2xl font-bold text-slate-900 mt-0.5">{stats.completedCount}</p>
//             <span className="text-[10px] text-slate-400 font-semibold">Evaluations archived</span>
//           </div>
//         </div>

//         {/* Average Rating */}
//         <div className="bg-white border rounded-2xl p-5 shadow-sm flex items-center gap-4">
//           <div className="w-12 h-12 bg-pink-50 border border-pink-100 rounded-full flex items-center justify-center text-pink-500 shrink-0">
//             <Award className="w-5 h-5" />
//           </div>
//           <div className="text-left">
//             <span className="block text-[10px] text-slate-400 font-bold uppercase tracking-wider">Average Rating</span>
//             <p className="text-2xl font-bold text-slate-900 mt-0.5">{stats.avgRating} <span className="text-xs text-slate-400 font-normal">/ 5</span></p>
//             <span className="text-[10px] text-slate-400 font-semibold">Team performance avg</span>
//           </div>
//         </div>

//       </div>

//       {/* FILTER SEARCH PANELS WITH COMPREHENSIVE SEARCHABLE DROPDOWNS */}
//       <div className="bg-white border rounded-xl p-5 shadow-[0_4px_20px_rgba(0,0,0,0.02)] space-y-4">
//         <div className="flex flex-wrap items-center gap-4">
          
//           <div className="w-48">
//             <span className="block text-[10px] text-slate-400 font-bold uppercase tracking-wider mb-1.5">Division</span>
//             <SelectDropdown options={divisions} value={selectedDiv} onChange={setSelectedDiv} placeholder="All Divisions" searchable={true} ChildClassName="h-9 text-xs border-slate-200 focus:border-red-400" />
//           </div>

//           <div className="w-48">
//             <span className="block text-[10px] text-slate-400 font-bold uppercase tracking-wider mb-1.5">Department</span>
//             <SelectDropdown options={departments} value={selectedDept} onChange={setSelectedDept} placeholder="All Departments" searchable={true} ChildClassName="h-9 text-xs border-slate-200 focus:border-red-400" />
//           </div>

//           <div className="w-48">
//             <span className="block text-[10px] text-slate-400 font-bold uppercase tracking-wider mb-1.5">Designation</span>
//             <SelectDropdown options={designations} value={selectedDesig} onChange={setSelectedDesig} placeholder="All Designations" searchable={true} ChildClassName="h-9 text-xs border-slate-200 focus:border-red-400" />
//           </div>

//           <div className="w-48">
//             <span className="block text-[10px] text-slate-400 font-bold uppercase tracking-wider mb-1.5">Review Status</span>
//             <SelectDropdown 
//               options={[
//                 { label: "Completed", value: "COMPLETED" },
//                 { label: "Under Review", value: "UNDER_REVIEW" },
//                 { label: "Draft", value: "DRAFT" },
//                 { label: "Disputed", value: "DISPUTED" }
//               ]} 
//               value={selectedStatus} 
//               onChange={setSelectedStatus} 
//               placeholder="All Reviews" 
//               searchable={true}
//               ChildClassName="h-9 text-xs border-slate-200 focus:border-red-400" 
//             />
//           </div>

//           <button onClick={resetAllFilters} className="flex items-center gap-1.5 px-4 py-2 border rounded-lg text-xs font-semibold h-9 mt-5 border-slate-200 hover:bg-slate-50">
//             <Filter className="h-4 w-4" /> Reset Filters
//           </button>
//         </div>

//         {/* Search query input */}
//         <div className="relative pt-1">
//           <Search className="w-4 h-4 text-slate-400 absolute left-3 top-3.5" />
//           <input 
//             type="text" 
//             value={searchQuery}
//             onChange={(e) => setSearchQuery(e.target.value)}
//             placeholder="Search team members dynamically by name or ID..."
//             className="w-full border border-slate-200 rounded-lg pl-9 pr-4 py-1.5 text-xs outline-none focus:ring-1 focus:ring-red-200 h-9"
//           />
//         </div>
//       </div>

//       {/* TEAM MEMBERS LIST TABLE */}
//       <div className="bg-white border rounded-xl shadow-sm overflow-hidden">
//         <div className="p-5 border-b flex justify-between items-center">
//           <span className="font-bold text-slate-800 text-sm">Team Members ({filteredReviews.length})</span>
//         </div>

//         <div className="overflow-x-auto custom-scrollbar">
//           <table className="w-full text-left border-collapse">
//             <thead>
//               <tr className="bg-slate-50/55 border-b text-[10px] font-bold text-slate-400 uppercase tracking-wider">
//                 <th className="py-4 px-6">Employee</th>
//                 <th className="py-4 px-4">Employee ID</th>
//                 <th className="py-4 px-4">Designation</th>
//                 <th className="py-4 px-4">Department</th>
//                 <th className="py-4 px-4">Reporting To</th>
//                 <th className="py-4 px-4 whitespace-nowrap">Status / Active Stage</th>
//                 <th className="py-4 px-4 text-center whitespace-nowrap">Reviews / Actions</th>
//                 <th className="py-4 px-6 text-center">Overall Rating</th>
//               </tr>
//             </thead>
//             <tbody className="divide-y text-xs text-slate-700">
//               {paginatedReviews.map((review) => {
//                 const user = review.userId || {};
//                 const activeStage = review.stages?.[review.currentStageIndex];
//                 const activeEvaluatorId = activeStage?.evaluatorId?._id || activeStage?.evaluatorId;
                
//                 // Evaluates if the current logged-in user is the assigned manager in active workflow stage
//                 const isMyTurnToEvaluate = review.status !== "COMPLETED" && activeEvaluatorId?.toString() === currentUser?._id?.toString();

//                 const finalEval = review.overallEvaluations?.[review.overallEvaluations.length - 1];
//                 const finalRating = finalEval?.overallScore ? finalEval.overallScore.toFixed(1) : "-";

//                 const reportingManagerName = user.reportingManagerId 
//                   ? `${user.reportingManagerId.firstName || ""} ${user.reportingManagerId.lastName || ""}`.trim()
//                   : "N/A";

//                 return (
//                   <tr key={review._id} className="hover:bg-slate-50/40 transition-colors">
//                     <td className="py-3 px-6">
//                       <div className="flex items-center gap-3">
//                         {user.profilePicture ? (
//                           <img src={user.profilePicture} alt="" className="w-8 h-8 rounded-full object-cover shrink-0" />
//                         ) : (
//                           <div className="w-8 h-8 rounded-full bg-primary flex items-center justify-center text-white text-[10px] font-bold shrink-0">
//                             {user.firstName?.[0]}{user.lastName?.[0]}
//                           </div>
//                         )}
//                         <div className="text-left">
//                           <p className="font-bold text-slate-900 whitespace-nowrap">{user.firstName} {user.lastName}</p>
//                           <p className="text-[10px] text-slate-400 font-medium whitespace-nowrap">{user.email}</p>
//                         </div>
//                       </div>
//                     </td>
//                     <td className="py-3 px-4 font-semibold text-slate-500 whitespace-nowrap text-left">{user.userId || "EMP-N/A"}</td>
//                     <td className="py-3 px-4 text-slate-500 whitespace-nowrap text-left">{user.designationId?.title || user.designationId?.name || "N/A"}</td>
//                     <td className="py-3 px-4 text-slate-500 whitespace-nowrap text-left">{user.departmentId?.name || "N/A"}</td>
//                     <td className="py-3 px-4 text-slate-500 font-medium whitespace-nowrap text-left">{reportingManagerName}</td>
                    
//                     {/* Status & Active stage */}
//                     <td className="py-3 px-4 text-left">
//                       <div className="flex flex-col gap-1">
//                         <span className={`w-fit text-[9px] font-bold px-2 py-0.5 rounded-full uppercase border ${
//                           review.status === "COMPLETED" 
//                             ? "bg-green-50 text-green-700 border-green-200" 
//                             : review.status === "UNDER_REVIEW"
//                             ? "bg-blue-50 text-blue-700 border-blue-200"
//                             : "bg-yellow-50 text-yellow-700 border-yellow-200"
//                         }`}>
//                           {review.status}
//                         </span>
//                         {activeStage && review.status !== "COMPLETED" ? (
//                           <span className="text-[10px] text-slate-400 font-semibold italic whitespace-nowrap">
//                             Waiting: {activeStage.stageName}
//                           </span>
//                         ) : (
//                           review.updatedAt && (
//                             <span className="text-[9px] text-slate-400 font-semibold italic whitespace-nowrap">
//                               Completed: {new Date(review.updatedAt).toLocaleDateString()}
//                             </span>
//                           )
//                         )}
//                       </div>
//                     </td>

//                     {/* Navigation Actions for appraisals */}
//                     <td className="py-3 px-4 text-center whitespace-nowrap">
//                       {isMyTurnToEvaluate ? (
//                         <button
//                           onClick={() => navigate(`reviews/evaluation/${review._id}`)}
//                           className="bg-orange-50 hover:bg-orange-100 text-orange-600 border border-orange-200 text-[10px] font-bold px-3 py-1 rounded-lg uppercase tracking-wider transition-all"
//                         >
//                           {review.period} Review: Pending Action
//                         </button>
//                       ) : (
//                         <button
//                           onClick={() => navigate(`reviews/evaluation/${review._id}`)}
//                           className={`flex items-center justify-center gap-1 mx-auto text-[10px] font-bold px-4 py-1.5 rounded-lg uppercase tracking-wider border transition-all ${
//                             review.status === "COMPLETED" 
//                               ? "bg-green-50 hover:bg-green-100 text-green-700 border-green-200" 
//                               : "bg-blue-50 hover:bg-blue-100 text-blue-700 border-blue-200"
//                           }`}
//                         >
//                           <span>View Details</span>
//                         </button>
//                       )}
//                     </td>
//                     <td className="py-3 px-6 text-center font-bold text-slate-900 whitespace-nowrap">
//                       {finalRating !== "-" ? (
//                         <span className="flex items-center justify-center gap-1">
//                           <span>{finalRating}</span>
//                           <span className="text-amber-500 text-sm">★</span>
//                         </span>
//                       ) : (
//                         <span className="text-slate-400">-</span>
//                       )}
//                     </td>
//                   </tr>
//                 );
//               })}

//               {paginatedReviews.length === 0 && (
//                 <tr>
//                   <td colSpan={8} className="text-center py-12 text-slate-400 text-xs">
//                     No team member evaluations match the current filter criteria.
//                   </td>
//                 </tr>
//               )}
//             </tbody>
//           </table>
//         </div>

//         {/* PAGINATION PANEL */}
//         {totalPages > 1 && (
//           <div className="p-4 border-t flex items-center justify-between gap-4 bg-slate-50/40">
//             <span className="text-xs text-slate-400">
//               Showing {((currentPage - 1) * itemsPerPage) + 1} to {Math.min(currentPage * itemsPerPage, filteredReviews.length)} of {filteredReviews.length} entries
//             </span>
//             <div className="flex items-center gap-2">
//               <button 
//                 disabled={currentPage === 1}
//                 onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
//                 className="p-1 border rounded hover:bg-white text-slate-400 disabled:opacity-40"
//               >
//                 <ChevronLeft className="w-4 h-4" />
//               </button>
//               {[...Array(totalPages)].map((_, idx) => (
//                 <button
//                   key={idx}
//                   onClick={() => setCurrentPage(idx + 1)}
//                   className={`px-2.5 py-1 text-xs font-bold rounded ${
//                     currentPage === idx + 1 ? "bg-primary text-white" : "border bg-white text-slate-600 hover:bg-slate-50"
//                   }`}
//                 >
//                   {idx + 1}
//                 </button>
//               ))}
//               <button 
//                 disabled={currentPage === totalPages}
//                 onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
//                 className="p-1 border rounded hover:bg-white text-slate-400 disabled:opacity-40"
//               >
//                 <ChevronRight className="w-4 h-4" />
//               </button>
//             </div>
//           </div>
//         )}
//       </div>

//     </div>
//   );
// }

import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { 
  Users, AlertCircle, CheckCircle, Award, 
  Search, Download, Network, ChevronLeft, ChevronRight, Filter, Star, X, ArrowRight
} from "lucide-react";
import api from "../../common/apiClient";
import SummaryApi from "../../common/SummaryApi";
import PageLoader from "../../helpers/PageLoader";
import { useSelector } from "react-redux";
import SelectDropdown from "../../customStyles/SelectDropdown";

// Helper to format system DB cycle keys to user-friendly labels
const formatCycleDisplay = (cycle) => {
  const mapping = {
    "1_WEEK": "Weekly",
    "2_WEEKS": "Bi-weekly",
    "1_MONTH": "Monthly",
    "2_MONTHS": "Bi-monthly",
    "3_MONTHS": "Quarterly",
    "6_MONTHS": "Half-Yearly",
    "12_MONTHS": "Annual"
  };
  return mapping[cycle] || cycle;
};

// Helper function to resolve KRA achievement rating based on image grading scale
const getAchievementRating = (percentage) => {
  if (percentage === null || percentage === undefined || isNaN(percentage)) return "N/A";
  if (percentage >= 110) return "Outstanding";
  if (percentage >= 100) return "Exceeds Expectations";
  if (percentage >= 90) return "Meets Expectations";
  if (percentage >= 75) return "Needs Improvement";
  return "Unsatisfactory";
};

// Helper function to return beautiful styling badge classes
const getRatingBadgeClass = (rating) => {
  switch (rating) {
    case "Outstanding": return "bg-emerald-100 text-emerald-800 border-emerald-200";
    case "Exceeds Expectations": return "bg-teal-50 text-teal-700 border-teal-200";
    case "Meets Expectations": return "bg-blue-50 text-blue-700 border-blue-200";
    case "Needs Improvement": return "bg-amber-50 text-amber-700 border-amber-200";
    case "Unsatisfactory": return "bg-red-50 text-red-700 border-red-200";
    default: return "bg-gray-50 text-gray-700 border-gray-200";
  }
};

export default function TeamReviews() {
  const navigate = useNavigate();
  const currentUser = useSelector((state) => state?.user?.user);

  const [loading, setLoading] = useState(true);
  const [teamReviews, setTeamReviews] = useState([]);
  const [selectedEmployeeAppraisals, setSelectedEmployeeAppraisals] = useState(null); // Stores intermediate drill-down reviews list
  const [selectedReview, setSelectedReview] = useState(null); // Stores selected review sheet details
  const [activeStageTab, setActiveStageTab] = useState("");
  const [skippingId, setSkippingId] = useState(null); 

  // Filters Master lists state
  const [departments, setDepartments] = useState([]);
  const [divisions, setDivisions] = useState([]);

  // Selected Filter States
  const [selectedDepartment, setSelectedDepartment] = useState("");
  const [selectedDivision, setSelectedDivision] = useState("");
  const [selectedEmployee, setSelectedEmployee] = useState("");
  const [selectedDesig, setSelectedDesig] = useState("");
  const [selectedYear, setSelectedYear] = useState("");
  const [selectedStatus, setSelectedStatus] = useState("");

  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  // Fetch reviews along with active structural departments and divisions from organization
  const loadTeamReviewsAndFilters = useCallback(async () => {
    setLoading(true);
    try {
      const url = SummaryApi.getReporteesReviews?.url || "/api/hr/kra/reviews/reportees";
      const [reviewsRes, deptRes, divRes] = await Promise.all([
        api.get(url),
        api.get(SummaryApi.getAllDepartments.url),
        api.get(SummaryApi.getAllDivisions.url)
      ]);

      if (reviewsRes.data.success) {
        setTeamReviews(reviewsRes.data.data || []);
      }
      if (deptRes.data.success) {
        setDepartments(deptRes.data.data.map(d => ({ label: d.name, value: d.name })));
      }
      if (divRes.data.success) {
        setDivisions(divRes.data.data.map(d => ({ label: d.name, value: d.name })));
      }
    } catch {
      toast.error("Failed to load team members' performance metrics.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadTeamReviewsAndFilters();
  }, [loadTeamReviewsAndFilters]);

  // Reset pagination to first page on filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, selectedDepartment, selectedDesig, selectedDivision, selectedYear, selectedStatus, selectedEmployee]);

  // Dynamic Designation options calculation
  const designations = useMemo(() => {
    const desigs = teamReviews.map(r => r.userId?.designationId?.title || r.userId?.designationId?.name).filter(Boolean);
    return [...new Set(desigs)].map(d => ({ label: d, value: d }));
  }, [teamReviews]);

  // Dynamic Employee options list
  const employees = useMemo(() => {
    const list = teamReviews.map(r => r.userId).filter(Boolean);
    const unique = [...new Map(list.map(item => [item._id, item])).values()];
    return unique.map(e => ({ label: `${e.firstName} ${e.lastName}`, value: e._id }));
  }, [teamReviews]);

  // Generates a continuous list of years starting from 2025 up to current year
  const years = useMemo(() => {
    const yearsList = [];
    const startYear = 2025; // System launch year
    const currentYear = new Date().getFullYear(); // Automatically gets current year

    for (let y = currentYear; y >= startYear; y--) {
      yearsList.push({ label: `FY ${y}`, value: y });
    }

    teamReviews.forEach(r => {
      if (r.year && r.year < startYear && !yearsList.some(item => item.value === r.year)) {
        yearsList.push({ label: `FY ${r.year}`, value: r.year });
      }
    });

    return yearsList.sort((a, b) => b.value - a.value);
  }, [teamReviews]);

  // Dynamic Stats Calculations (Accurately counts unique members and pending manager actions)
  const stats = useMemo(() => {
    const uniqueUserIds = [...new Set(teamReviews.map(r => r.userId?._id).filter(Boolean))];
    const totalCount = uniqueUserIds.length;
    
    const pendingCount = teamReviews.filter(r => {
      const activeStage = r.stages?.[r.currentStageIndex];
      const activeEvaluatorId = activeStage?.evaluatorId?._id || activeStage?.evaluatorId;
      return r.status !== "COMPLETED" && activeEvaluatorId?.toString() === currentUser?._id?.toString();
    }).length;

    const completedCount = teamReviews.filter(r => r.status === "COMPLETED").length;

    const ratings = teamReviews
      .map(r => {
        const finalEval = r.overallEvaluations?.[r.overallEvaluations.length - 1];
        return finalEval?.overallScore || 0;
      })
      .filter(score => score > 0);

    const avgRating = ratings.length > 0 
      ? (ratings.reduce((sum, val) => sum + val, 0) / ratings.length).toFixed(1) 
      : "0.0";

    return { totalCount, pendingCount, completedCount, avgRating };
  }, [teamReviews, currentUser]);

  // Front-end filters logic
  const filteredReviews = useMemo(() => {
    return teamReviews.filter(r => {
      const user = r.userId || {};
      const fullName = `${user.firstName || ""} ${user.lastName || ""}`.toLowerCase();
      const matchesSearch = fullName.includes(searchQuery.toLowerCase()) || 
                            (user.userId && user.userId.toLowerCase().includes(searchQuery.toLowerCase()));
      
      const userDept = user.departmentId?.name || "";
      const matchesDept = !selectedDepartment || userDept === selectedDepartment;

      const userDesig = user.designationId?.title || user.designationId?.name || "";
      const matchesDesig = !selectedDesig || userDesig === selectedDesig;

      const userDiv = user.divisionId?.name || "";
      const matchesDiv = !selectedDivision || userDiv === selectedDivision;

      const matchesYear = !selectedYear || r.year === Number(selectedYear);
      const matchesStatus = !selectedStatus || r.status === selectedStatus;
      const matchesEmployee = !selectedEmployee || user._id === selectedEmployee;

      return matchesSearch && matchesDept && matchesDesig && matchesDiv && matchesYear && matchesStatus && matchesEmployee;
    });
  }, [teamReviews, searchQuery, selectedDepartment, selectedDesig, selectedDivision, selectedYear, selectedStatus, selectedEmployee]);

  // Groups filtered reviews dynamically by Employee ID and Year
  const employeeGroupedReviews = useMemo(() => {
    const map = {};
    filteredReviews.forEach(review => {
      const empId = review.userId?._id;
      if (!empId) return;

      const groupKey = `${empId}_${review.year}`;

      if (!map[groupKey]) {
        map[groupKey] = {
          employee: review.userId,
          year: review.year, 
          appraisals: [],
          completedCount: 0,
          pendingCount: 0
        };
      }
      map[groupKey].appraisals.push(review);
      if (review.status === "COMPLETED") {
        map[groupKey].completedCount += 1;
      }

      // Check if there is an active stage awaiting this manager's turn
      const activeStage = review.stages?.[review.currentStageIndex];
      const activeEvaluatorId = activeStage?.evaluatorId?._id || activeStage?.evaluatorId;
      const isMyTurn = review.status !== "COMPLETED" && activeEvaluatorId?.toString() === currentUser?._id?.toString();
      if (isMyTurn) {
        map[groupKey].pendingCount += 1;
      }
    });

    return Object.values(map).map(item => {
      let scoreSum = 0;
      let scoreCount = 0;
      let totalWeightageSum = 0;
      item.appraisals.forEach(r => {
        const latestEval = r.overallEvaluations?.[r.overallEvaluations.length - 1];
        if (latestEval && latestEval.overallScore) {
          scoreSum += latestEval.overallScore;
          scoreCount += 1;
          totalWeightageSum += r.kras?.reduce((sum, k) => sum + Number(k.weightage || 0), 0) || 100;
        }
      });
      const avgScore = scoreCount > 0 ? (scoreSum / scoreCount).toFixed(2) : "-";
      return {
        ...item,
        avgScore,
        totalWeightageSum: scoreCount > 0 ? (totalWeightageSum / scoreCount) : 100
      };
    });
  }, [filteredReviews, currentUser]);

  // Front-end Pagination (Applies to grouped employee list)
  const paginatedGroupedReviews = useMemo(() => {
    const startIdx = (currentPage - 1) * itemsPerPage;
    return employeeGroupedReviews.slice(startIdx, startIdx + itemsPerPage);
  }, [employeeGroupedReviews, currentPage]);

  const totalPages = Math.ceil(employeeGroupedReviews.length / itemsPerPage);

  const resetAllFilters = () => {
    setSelectedDepartment("");
    setSelectedDivision("");
    setSelectedEmployee("");
    setSelectedDesig("");
    setSelectedYear("");
    setSelectedStatus("");
    setSearchQuery("");
  };

  // Helper to identify if the active stage is a mandatory Self Appraisal stage
  const isSelfAssessmentStage = useCallback((row) => {
    const currentStage = row?.stages?.[row.currentStageIndex];
    if (!currentStage) return false;

    const stageKey = String(currentStage.stageKey).toLowerCase();
    const isSelfKey = stageKey.includes("self") || stageKey.includes("employee");

    const evaluatorId = currentStage.evaluatorId?._id || currentStage.evaluatorId;
    const userId = row.userId?._id || row.userId;
    const isSelfEvaluator = evaluatorId && userId && evaluatorId.toString() === userId.toString();

    return isSelfKey || isSelfEvaluator;
  }, []);

  const handleOpenDetailsModal = useCallback((review) => {
    setSelectedReview(review);

    if (
      review.overallEvaluations &&
      review.overallEvaluations.length > 0
    ) {
      setActiveStageTab(review.overallEvaluations[0].stageKey);
    } else {
      setActiveStageTab("");
    }
  }, []);

  // Resolves the evaluator name dynamically with strict populates check
  const getEvaluatorName = (stageKey, evaluatorId) => {
    if (!selectedReview?.stages) return "Evaluator";
    
    // 1. Searches inside active workflow stages array
    const stageObj = selectedReview.stages.find(s => s.stageKey === stageKey);
    if (stageObj?.evaluatorId) {
      const evalObj = stageObj.evaluatorId;
      if (evalObj.firstName) {
        return `${evalObj.firstName} ${evalObj.lastName || ""}`.trim();
      }
    }
    
    // 2. Direct fallback to inline evaluations object
    if (evaluatorId && typeof evaluatorId === 'object' && evaluatorId.firstName) {
      return `${evaluatorId.firstName} ${evaluatorId.lastName || ""}`.trim();
    }
    
    return "Evaluator";
  };

  // Stage Skip/Bypass Handler
  const handleSkipStage = useCallback(async (review) => {
    if (review.status === "COMPLETED") {
      return toast.warning("This review sheet is already completed and locked.");
    }

    if (isSelfAssessmentStage(review)) {
      return toast.error("The Self Assessment stage is mandatory and cannot be skipped.");
    }

    const currentStageName = review.stages[review.currentStageIndex]?.stageName || "Active Stage";
    if (!window.confirm(`Are you sure you want to bypass/skip the current evaluation stage (${currentStageName}) for this employee?`)) {
      return;
    }

    setSkippingId(review._id);
    try {
      const url = SummaryApi.skipKraReviewStage?.url || "/api/hr/kra/reviews/skip";
      const res = await api.post(url, { reviewId: review._id });
      if (res.data.success) {
        toast.success("Active review stage bypassed successfully.");
        
        const updatedReview = res.data.data;
        setTeamReviews(prev => prev.map(r => r._id === review._id ? updatedReview : r));

        if (selectedEmployeeAppraisals) {
          setSelectedEmployeeAppraisals(prev => {
            const updatedAppraisals = prev.appraisals.map(r => r._id === review._id ? updatedReview : r);
            return { ...prev, appraisals: updatedAppraisals };
          });
        }
        if (selectedReview && selectedReview._id === review._id) {
          setSelectedReview(updatedReview);
        }
      }
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to bypass/skip evaluation stage.");
    } finally {
      setSkippingId(null);
    }
  }, [selectedReview, selectedEmployeeAppraisals, isSelfAssessmentStage]); 

  const getSelectedReviewActiveWeightageSum = useMemo(() => {
    if (!selectedReview || !selectedReview.kras) return 100;
    return selectedReview.kras.reduce((sum, k) => sum + Number(k.weightage || 0), 0);
  }, [selectedReview]);

  if (loading) return <PageLoader />;

  return (
  <>
    <div className="p-2 space-y-6 animate-in fade-in duration-300">
      
      {/* HEADER CONTROLS */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-xl font-bold text-slate-900 tracking-tight text-left">Team Performance Reviews</h1>
          <p className="text-xs text-slate-400 mt-1 text-left">View and manage your team members and their performance progress.</p>
        </div>
        <div className="flex items-center gap-3">
          <button className="flex items-center gap-1.5 bg-white border border-slate-200 hover:bg-slate-50 text-slate-700 text-xs font-bold px-4 py-2 rounded-lg shadow-sm transition-all">
            <Network className="w-4 h-4" />
            <span>Team Structure</span>
          </button>
          <button className="flex items-center gap-1.5 bg-white border border-slate-200 hover:bg-slate-50 text-slate-700 text-xs font-bold px-4 py-2 rounded-lg shadow-sm transition-all">
            <Download className="w-4 h-4" />
            <span>Export</span>
          </button>
        </div>
      </div>

      {/* STATS ROW */}
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-6">
        
        <div className="bg-white border rounded-2xl p-5 shadow-sm flex items-center gap-4">
          <div className="w-12 h-12 bg-blue-50 border border-blue-100 rounded-full flex items-center justify-center text-blue-600 shrink-0">
            <Users className="w-5 h-5" />
          </div>
          <div className="text-left">
            <span className="block text-[10px] text-slate-400 font-bold uppercase tracking-wider">Total Members</span>
            <p className="text-2xl font-bold text-slate-900 mt-0.5">{stats.totalCount}</p>
            <span className="text-[10px] text-slate-400 font-semibold">Active workspace</span>
          </div>
        </div>

        <div className="bg-white border rounded-2xl p-5 shadow-sm flex items-center gap-4">
          <div className="w-12 h-12 bg-amber-50 border border-amber-100 rounded-full flex items-center justify-center text-amber-600 shrink-0">
            <AlertCircle className="w-5 h-5" />
          </div>
          <div className="text-left">
            <span className="block text-[10px] text-slate-400 font-bold uppercase tracking-wider">Reviews Pending</span>
            <p className="text-2xl font-bold text-slate-900 mt-0.5">{stats.pendingCount}</p>
            <span className="text-[10px] text-red-600 font-semibold">Requires your action</span>
          </div>
        </div>

        <div className="bg-white border rounded-2xl p-5 shadow-sm flex items-center gap-4">
          <div className="w-12 h-12 bg-indigo-50 border border-indigo-100 rounded-full flex items-center justify-center text-indigo-600 shrink-0">
            <CheckCircle className="w-5 h-5" />
          </div>
          <div className="text-left">
            <span className="block text-[10px] text-slate-400 font-bold uppercase tracking-wider">Completed Reviews</span>
            <p className="text-2xl font-bold text-slate-900 mt-0.5">{stats.completedCount}</p>
            <span className="text-[10px] text-slate-400 font-semibold">Evaluations archived</span>
          </div>
        </div>

        <div className="bg-white border rounded-2xl p-5 shadow-sm flex items-center gap-4">
          <div className="w-12 h-12 bg-pink-50 border border-pink-100 rounded-full flex items-center justify-center text-pink-500 shrink-0">
            <Award className="w-5 h-5" />
          </div>
          <div className="text-left">
            <span className="block text-[10px] text-slate-400 font-bold uppercase tracking-wider">Average Rating</span>
            <p className="text-2xl font-bold text-slate-900 mt-0.5">{stats.avgRating} <span className="text-xs text-slate-400 font-normal">{parseFloat(stats.avgRating) <= 5 ? "/ 5" : "/ 100"}</span></p>
            <span className="text-[10px] text-slate-400 font-semibold">Team performance avg</span>
          </div>
        </div>

      </div>

      {/* FILTER SEARCH PANELS */}
      <div className="bg-white border rounded-xl p-5 shadow-[0_4px_20px_rgba(0,0,0,0.02)] space-y-4">
        <div className="flex flex-wrap items-center gap-4">
          
          <div className="w-48 text-left">
            <span className="block text-[10px] text-slate-400 font-bold uppercase tracking-wider mb-1.5">Review Year</span>
            <SelectDropdown options={years} value={selectedYear} valueKey="value" labelKey="label" onChange={setSelectedYear} placeholder="All Years" searchable={true} ChildClassName="h-9 text-xs border-slate-200 focus:border-red-400" />
          </div>

          <div className="w-48 text-left">
            <span className="block text-[10px] text-slate-400 font-bold uppercase tracking-wider mb-1.5">Division</span>
            <SelectDropdown options={divisions} value={selectedDivision} valueKey="value" labelKey="label" onChange={setSelectedDivision} placeholder="All Divisions" searchable={true} ChildClassName="h-9 text-xs border-slate-200 focus:border-red-400" />
          </div>

          <div className="w-48 text-left">
            <span className="block text-[10px] text-slate-400 font-bold uppercase tracking-wider mb-1.5">Department</span>
            <SelectDropdown options={departments} value={selectedDepartment} valueKey="value" labelKey="label" onChange={setSelectedDepartment} placeholder="All Departments" searchable={true} ChildClassName="h-9 text-xs border-slate-200 focus:border-red-400" />
          </div>

          <div className="w-48 text-left">
            <span className="block text-[10px] text-slate-400 font-bold uppercase tracking-wider mb-1.5">Designation</span>
            <SelectDropdown options={designations} value={selectedDesig} valueKey="value" labelKey="label" onChange={setSelectedDesig} placeholder="All Designations" searchable={true} ChildClassName="h-9 text-xs border-slate-200 focus:border-red-400" />
          </div>

          <div className="w-48 text-left">
            <span className="block text-[10px] text-slate-400 font-bold uppercase tracking-wider mb-1.5">Employee Selection</span>
            <SelectDropdown options={employees} value={selectedEmployee} valueKey="value" labelKey="label" onChange={setSelectedEmployee} placeholder="All Employees" searchable={true} ChildClassName="h-9 text-xs border-slate-200 focus:border-red-400" />
          </div>

          <div className="w-48 text-left">
            <span className="block text-[10px] text-slate-400 font-bold uppercase tracking-wider mb-1.5">Review Status</span>
            <SelectDropdown 
              options={[
                { label: "Completed", value: "COMPLETED" },
                { label: "Under Review", value: "UNDER_REVIEW" },
                { label: "Draft", value: "DRAFT" },
                { label: "Disputed", value: "DISPUTED" }
              ]} 
              value={selectedStatus} 
              valueKey="value" labelKey="label"
              onChange={setSelectedStatus} 
              placeholder="All Reviews" 
              searchable={true}
              ChildClassName="h-9 text-xs border-slate-200 focus:border-red-400" 
            />
          </div>

          <button onClick={resetAllFilters} className="flex items-center gap-1.5 px-4 py-2 border rounded-lg text-xs font-semibold h-9 mt-5 border-slate-200 hover:bg-slate-50">
            <Filter className="h-4 w-4" /> Reset Filters
          </button>
        </div>

        <div className="relative pt-1 text-left">
          <Search className="w-4 h-4 text-slate-400 absolute left-3 top-3.5" />
          <input 
            type="text" 
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search team members dynamically by name or ID..."
            className="w-full border border-slate-200 rounded-lg pl-9 pr-4 py-1.5 text-xs outline-none focus:ring-1 focus:ring-red-200 h-9"
          />
        </div>
      </div>

      {/* TEAM MEMBERS LIST TABLE */}
      <div className="bg-white border rounded-xl shadow-sm overflow-hidden">
        <div className="p-5 border-b flex justify-between items-center">
          <span className="font-bold text-slate-800 text-sm">Team Members ({employeeGroupedReviews.length})</span>
        </div>

        <div className="overflow-x-auto custom-scrollbar">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="bg-slate-50/55 border-b text-[10px] font-bold text-slate-400 uppercase tracking-wider">
                <th className="py-4 px-6">Employee</th>
                <th className="py-4 px-4">Employee ID</th>
                <th className="py-4 px-4">Designation</th>
                <th className="py-4 px-4">Department</th>
                <th className="py-4 px-4 font-bold">Reporting To</th>
                <th className="py-4 px-4 whitespace-nowrap">Appraisal Progress</th>
                <th className="py-4 px-4 text-center whitespace-nowrap">Manage Appraisals</th>
                <th className="py-4 px-6 text-center">Average Score</th>
              </tr>
            </thead>
            <tbody className="divide-y text-xs text-slate-700">
              {paginatedGroupedReviews.map((row) => {
                const user = row.employee || {};
                const finalRatingVal = row.avgScore !== "-" ? parseFloat(row.avgScore) : null;
                const isOutOfFive = finalRatingVal !== null && finalRatingVal <= 5;

                const reportingManagerName = user.reportingManagerId 
                  ? `${user.reportingManagerId.firstName || ""} ${user.reportingManagerId.lastName || ""}`.trim()
                  : "N/A";

                return (
                  <tr key={`${user._id}_${row.year}`} className="hover:bg-slate-50/40 transition-colors">
                    <td className="py-3 px-6">
                      <div className="flex items-center gap-3">
                        {user.profilePicture ? (
                          <img src={user.profilePicture} alt="" className="w-8 h-8 rounded-full object-cover shrink-0" />
                        ) : (
                          <div className="w-8 h-8 rounded-full bg-primary flex items-center justify-center text-white text-[10px] font-bold shrink-0">
                            {user.firstName?.[0]}{user.lastName?.[0]}
                          </div>
                        )}
                        <div className="text-left">
                          <p className="font-bold text-slate-900 whitespace-nowrap">{user.firstName} {user.lastName}</p>
                          <p className="text-[10px] text-slate-400 font-medium whitespace-nowrap">{user.email}</p>
                        </div>
                      </div>
                    </td>
                    <td className="py-3 px-4 font-semibold text-slate-500 whitespace-nowrap text-left">{user.userId || "EMP-N/A"}</td>
                    <td className="py-3 px-4 text-slate-500 whitespace-nowrap text-left">{user.designationId?.title || user.designationId?.name || "N/A"}</td>
                    <td className="py-3 px-4 text-slate-500 whitespace-nowrap text-left">{user.departmentId?.name || "N/A"}</td>
                    <td className="py-3 px-4 text-slate-500 font-medium whitespace-nowrap text-left">{reportingManagerName}</td>
                    
                    <td className="py-3 px-4 text-left">
                      <div className="flex flex-col gap-1">
                        <span className="text-slate-600 font-bold whitespace-nowrap">
                          {row.completedCount} / {row.appraisals.length} Completed (FY {row.year})
                        </span>
                        {row.pendingCount > 0 && (
                          <span className="text-[10px] text-orange-600 font-extrabold bg-orange-50 border border-orange-200 px-1.5 py-0.2 rounded w-fit uppercase">
                            {row.pendingCount} Action Pending
                          </span>
                        )}
                      </div>
                    </td>

                    <td className="py-3 px-4 text-center whitespace-nowrap">
                      <button
                        onClick={() => setSelectedEmployeeAppraisals(row)} 
                        className="bg-primary hover:bg-secondary text-white border text-[10px] font-bold px-3 py-1.5 rounded-lg uppercase tracking-wider transition-all"
                      >
                        Manage Appraisals
                      </button>
                    </td>

                    <td className="py-3 px-6 text-center font-bold text-slate-900 whitespace-nowrap">
                      {finalRatingVal !== null ? (
                        <div className="flex flex-col items-center gap-1">
                          <span className="flex items-center justify-center gap-1 bg-slate-50 px-2.5 py-0.5 border rounded-lg text-[10px] font-bold">
                            <span>{finalRatingVal.toFixed(2)}</span>
                            <span className="text-amber-500 font-normal">
                              {isOutOfFive ? "★" : `/ ${row.totalWeightageSum.toFixed(0)}`}
                            </span>
                          </span>
                          {!isOutOfFive && (
                            <span className={`text-[8px] font-bold px-1.5 py-0.5 rounded border uppercase tracking-wider ${getRatingBadgeClass(getAchievementRating((finalRatingVal / row.totalWeightageSum) * 100))}`}>
                              {getAchievementRating((finalRatingVal / row.totalWeightageSum) * 100)}
                            </span>
                          )}
                        </div>
                      ) : (
                        <span className="text-slate-400">-</span>
                      )}
                    </td>
                  </tr>
                );
              })}

              {paginatedGroupedReviews.length === 0 && (
                <tr>
                  <td colSpan={8} className="text-center py-12 text-slate-400 text-xs">
                    No team member evaluations match the current filter criteria.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* PAGINATION PANEL */}
        {totalPages > 1 && (
          <div className="p-4 border-t flex items-center justify-between gap-4 bg-slate-50/40">
            <span className="text-xs text-slate-400">
              Showing {((currentPage - 1) * itemsPerPage) + 1} to {Math.min(currentPage * itemsPerPage, employeeGroupedReviews.length)} of {employeeGroupedReviews.length} entries
            </span>
            <div className="flex items-center gap-2">
              <button 
                disabled={currentPage === 1}
                onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                className="p-1 border rounded hover:bg-white text-slate-400 disabled:opacity-40"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              {[...Array(totalPages)].map((_, idx) => (
                <button
                  key={idx}
                  onClick={() => setCurrentPage(idx + 1)}
                  className={`px-2.5 py-1 text-xs font-bold rounded ${
                    currentPage === idx + 1 ? "bg-primary text-white" : "border bg-white text-slate-600 hover:bg-slate-50"
                  }`}
                >
                  {idx + 1}
                </button>
              ))}
              <button 
                disabled={currentPage === totalPages}
                onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                className="p-1 border rounded hover:bg-white text-slate-400 disabled:opacity-40"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>

      {/* INTERMEDIATE SUB-LIST MODAL */}
      {selectedEmployeeAppraisals && (
        <div className="fixed inset-0 top-[57px] md:top-[65px] z-[70] flex items-center justify-center p-3 sm:p-4 bg-black/50 backdrop-blur-sm animate-in fade-in duration-200">
          <div className="bg-white w-full max-w-3xl rounded-2xl shadow-2xl border flex flex-col max-h-[88vh] overflow-hidden animate-in fade-in zoom-in-95 duration-200">
            
            <div className="p-4 sm:p-5 border-b flex justify-between items-center shrink-0">
              <div className="text-left">
                <h3 className="font-bold text-gray-900 text-sm sm:text-base leading-tight">
                  Team Appraisal Cycles - {selectedEmployeeAppraisals.employee?.firstName} {selectedEmployeeAppraisals.employee?.lastName} (FY {selectedEmployeeAppraisals.year})
                </h3>
                <p className="text-[10px] text-gray-400 font-bold uppercase mt-0.5">
                  Showing initiated evaluation cycles
                </p>
              </div>
              <button 
                onClick={() => setSelectedEmployeeAppraisals(null)}
                className="p-1.5 hover:bg-red-50 text-gray-400 hover:text-red-600 rounded-lg transition-colors border"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="p-4 sm:p-6 space-y-4 overflow-y-auto flex-1 custom-scrollbar bg-slate-50/50">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {selectedEmployeeAppraisals.appraisals?.map((review) => {
                  const isCompleted = review.status === "COMPLETED";
                  const currentStageName = review.stages?.[review.currentStageIndex]?.stageName || "In Progress";
                  
                  const latestEval = review.overallEvaluations?.[review.overallEvaluations.length - 1];
                  const hasScore = latestEval && latestEval.overallScore !== undefined;
                  const isOutOfFive = latestEval?.overallScore <= 5;
                  const activeWeightageSum = review.kras?.reduce((sum, k) => sum + Number(k.weightage || 0), 0) || 100;

                  // Evaluates if the current logged-in user is the assigned evaluator in active stage
                  const activeStage = review.stages?.[review.currentStageIndex];
                  const activeEvaluatorId = activeStage?.evaluatorId?._id || activeStage?.evaluatorId;
                  const isMyTurnToEvaluate = review.status !== "COMPLETED" && activeEvaluatorId?.toString() === currentUser?._id?.toString();

                  const activeEvaluatorName = activeStage?.evaluatorId?.firstName 
                    ? `${activeStage.evaluatorId.firstName} ${activeStage.evaluatorId.lastName || ""}`.trim() 
                    : "N/A";

                  return (
                    <div 
                      key={review._id} 
                      className="bg-white border rounded-xl p-4 shadow-sm flex flex-col justify-between space-y-4 hover:border-primary/40 transition-colors"
                    >
                      <div className="space-y-2.5 text-left">
                        <div className="flex justify-between items-start gap-2">
                          <div>
                            <span className="text-[10px] bg-slate-100 text-slate-700 border border-slate-200 font-bold px-2 py-0.5 rounded uppercase">
                              FY {review.year}
                            </span>
                            <h4 className="font-bold text-gray-900 text-sm sm:text-base leading-tight mt-1">
                              {review.period} Appraisal
                            </h4>
                          </div>
                          
                          <span className={`text-[9px] font-bold px-2 py-0.5 rounded-full border uppercase tracking-wider ${
                            isCompleted 
                              ? "bg-green-50 text-green-700 border-green-200" 
                              : "bg-blue-50 text-blue-700 border-blue-200"
                          }`}>
                            {review.status}
                          </span>
                        </div>

                        <div className="space-y-1.5 text-xs border-t pt-2 text-slate-500 font-medium">
                          <p>
                            Active Step: <strong className="text-slate-800">{isCompleted ? "Completed" : currentStageName}</strong>
                          </p>
                          {!isCompleted && (
                            <p>
                              Current Evaluator: <strong className="text-slate-700">{activeEvaluatorName}</strong>
                            </p>
                          )}
                          {hasScore && (
                            <div className="flex items-center gap-1.5 pt-1">
                              <span className="text-slate-500 font-semibold">Latest Score:</span>
                              <span className="flex items-center gap-0.5 bg-amber-50 text-amber-700 border border-amber-200 font-bold px-2 py-0.5 rounded text-[11px]">
                                <Star className="w-3.5 h-3.5 fill-amber-500 text-amber-500" />
                                {latestEval.overallScore?.toFixed(2)} {isOutOfFive ? "/ 5.00" : `/ ${activeWeightageSum}`}
                              </span>
                            </div>
                          )}
                        </div>
                      </div>

                      <div className="pt-3 border-t flex justify-end gap-2 shrink-0">
                        {!isCompleted && !isSelfAssessmentStage(review) && (
                          <button
                            onClick={() => handleSkipStage(review)}
                            disabled={skippingId === review._id}
                            className="px-3 py-1.5 bg-amber-500 hover:bg-amber-600 text-white rounded-lg text-[10px] font-bold uppercase transition-colors"
                          >
                            {skippingId === review._id ? "Bypassing..." : "Skip Stage"}
                          </button>
                        )}
                        
                        {/* Evaluate KRA Option (Visible only if it is this manager's active turn to review) */}
                        {isMyTurnToEvaluate && (
                          <button
                            onClick={() => {
                              setSelectedEmployeeAppraisals(null);
                              navigate(`reviews/evaluation/${review._id}`); // Navigates to the evaluation form relative path
                            }}
                            className="px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-[10px] font-bold uppercase transition-colors flex items-center gap-1"
                          >
                            <span>Evaluate KRA</span>
                            <ArrowRight className="w-3 h-3" />
                          </button>
                        )}

                        <button
                          onClick={() => {
                            setSelectedEmployeeAppraisals(null); 
                            handleOpenDetailsModal(review); 
                          }}
                          className="px-3 py-1.5 bg-slate-50 hover:bg-slate-100 text-slate-700 border rounded-lg text-[10px] font-bold uppercase transition-colors flex items-center gap-1"
                        >
                          <span>Open Sheet</span>
                          <ArrowRight className="w-3 h-3" />
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="p-4 border-t bg-slate-50 flex justify-end shrink-0">
              <button
                onClick={() => setSelectedEmployeeAppraisals(null)}
                className="px-4 py-2 bg-white hover:bg-slate-50 text-slate-700 border rounded-lg text-xs font-bold uppercase transition-colors"
              >
                Close View
              </button>
            </div>

          </div>
        </div>
      )}

      {/* COMPREHENSIVE APPRAISAL REVIEW DETAILS MODAL WITH STAGE MATRIX */}
      {selectedReview && (
        <div className="fixed inset-0 top-[57px] md:top-[65px] z-[75] flex items-center justify-center p-3 sm:p-4 bg-black/50 backdrop-blur-sm animate-in fade-in duration-200">
          <div className="bg-white w-full max-w-4xl rounded-2xl shadow-2xl border flex flex-col max-h-[92vh] sm:max-h-[88vh] overflow-hidden animate-in fade-in zoom-in-95 duration-200">
            
            <div className="p-4 sm:p-5 border-b flex justify-between items-center shrink-0">
              <div className="flex gap-3 items-center">
                <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-full bg-emerald-50 text-emerald-600 flex items-center justify-center shrink-0 border border-emerald-100">
                  <Award className="w-5 h-5" />
                </div>
                <div className="text-left">
                  <h3 className="font-bold text-gray-900 text-sm sm:text-base leading-tight">
                    {selectedReview.userId?.firstName} {selectedReview.userId?.lastName} - Appraisal Review
                  </h3>
                  <p className="text-[9px] sm:text-[10px] text-gray-400 font-bold uppercase mt-0.5">
                    {selectedReview.period} • FY {selectedReview.year} Appraisal Details
                  </p>
                </div>
              </div>
              <button 
                onClick={() => setSelectedReview(null)}
                className="p-1 sm:p-1.5 hover:bg-red-50 text-gray-400 hover:text-red-600 rounded-lg transition-colors border"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            {/* Modal Body */}
            <div className="p-4 sm:p-6 space-y-6 overflow-y-auto flex-1 custom-scrollbar bg-slate-50/50">
              
              <div className="bg-white border rounded-xl p-4 shadow-sm grid grid-cols-2 md:grid-cols-4 gap-4 text-xs text-left">
                <div>
                  <span className="block text-[10px] text-gray-400 font-bold uppercase">Employee ID</span>
                  <span className="font-bold text-gray-800">{selectedReview.userId?.userId || "N/A"}</span>
                </div>
                <div>
                  <span className="block text-[10px] text-gray-400 font-bold uppercase">Department</span>
                  <span className="font-bold text-gray-800">{selectedReview.userId?.departmentId?.name || "N/A"}</span>
                </div>
                <div>
                  <span className="block text-[10px] text-gray-400 font-bold uppercase">Designation</span>
                  <span className="font-bold text-gray-800">{selectedReview.userId?.designationId?.title || "N/A"}</span>
                </div>
                <div>
                  <span className="block text-[10px] text-gray-400 font-bold uppercase">Reporting Manager</span>
                  <span className="font-bold text-gray-800">
                    {selectedReview.userId?.reportingManagerId 
                      ? `${selectedReview.userId.reportingManagerId.firstName} ${selectedReview.userId.reportingManagerId.lastName}`
                      : "N/A"}
                  </span>
                </div>
              </div>

              {/* STAGES & OVERALL EVALUATION MATRIX */}
              {selectedReview.overallEvaluations && selectedReview.overallEvaluations.length > 0 && (
                <div className="bg-white border rounded-xl p-4 shadow-sm space-y-4">
                  <div className="flex justify-between items-center border-b pb-2">
                    <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider block text-left">Stage-wise Overall Evaluations</span>
                  </div>

                  <div className="flex flex-wrap gap-2 border-b pb-2">
                    {selectedReview.overallEvaluations.map((evalItem) => {
                      const isActive = activeStageTab === evalItem.stageKey;
                      const stageObj = selectedReview.stages?.find(s => s.stageKey === evalItem.stageKey);
                      return (
                        <button
                          key={evalItem.stageKey}
                          type="button"
                          onClick={() => setActiveStageTab(evalItem.stageKey)}
                          className={`px-3 py-1.5 text-xs font-bold rounded-lg transition-colors border ${
                            isActive 
                              ? "bg-red-50 text-red-600 border-red-200" 
                              : "bg-slate-50 hover:bg-slate-100 text-slate-600 border-slate-200"
                          }`}
                        >
                          {stageObj?.stageName || evalItem.stageKey.replace("_", " ")}
                        </button>
                      );
                    })}
                  </div>

                  {/* Selected Stage Tab Content */}
                  {selectedReview.overallEvaluations.map((evalItem) => {
                    if (activeStageTab !== evalItem.stageKey) return null;
                    const isOutOfFive = evalItem.overallScore <= 5;
                    
                    return (
                      <div key={evalItem.stageKey} className="space-y-4 animate-in fade-in duration-200 text-xs text-left">
                        <div className="flex items-center gap-3">
                          <span className="font-medium text-slate-500">Overall Score:</span>
                          <span className="flex items-center gap-1 bg-amber-50 text-amber-700 border border-amber-200 font-bold px-3 py-1 rounded-lg text-sm">
                            <Star className="w-4 h-4 fill-amber-500 text-amber-500" />
                            {evalItem.overallScore?.toFixed(2)} {isOutOfFive ? "/ 5.00" : `/ ${getSelectedReviewActiveWeightageSum.toFixed(2)}`}
                          </span>
                        </div>

                        {/* Feedbacks */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div className="bg-slate-50 p-3 rounded-lg border border-slate-100">
                            <span className="block font-bold text-slate-500 mb-1">Overall Feedback</span>
                            <p className="text-slate-700 italic leading-relaxed">"{evalItem.overallFeedback || "No feedback provided."}"</p>
                          </div>
                          <div className="bg-slate-50 p-3 rounded-lg border border-slate-100">
                            <span className="block font-bold text-slate-500 mb-1">Next Year's Goals / Focus</span>
                            <p className="text-slate-700 italic leading-relaxed">"{evalItem.nextYearGoalsFeedback || "No feedback provided."}"</p>
                          </div>
                        </div>

                        {evalItem.additionalFeedback && (
                          <div className="bg-slate-50 p-3 rounded-lg border border-slate-100">
                            <span className="block font-bold text-slate-500 mb-1">Additional Feedback</span>
                            <p className="text-slate-700 italic leading-relaxed">"{evalItem.additionalFeedback}"</p>
                          </div>
                        )}

                        {/* Attachments */}
                        {evalItem.attachments && evalItem.attachments.length > 0 && (
                          <div className="space-y-2">
                            <span className="block font-bold text-slate-500">Attachments</span>
                            <div className="flex flex-wrap gap-2">
                              {evalItem.attachments.map((file, fIdx) => (
                                <a 
                                  key={fIdx}
                                  href={file.url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="inline-flex items-center gap-1.5 bg-white border hover:bg-slate-50 text-red-600 px-3 py-1.5 rounded-lg font-bold shadow-sm transition-colors"
                                >
                                  <Download className="w-3.5 h-3.5" />
                                  <span>{file.name}</span>
                                </a>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}

              {/* INDIVIDUAL KRA RATING BREAKDOWN */}
              <div className="space-y-3">
                <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider block text-left">Key Result Areas (KRAs) Assessment Details</span>
                
                {selectedReview.kras?.map((kra, kIdx) => (
                  <div key={kIdx} className="bg-white border rounded-xl p-4 shadow-sm space-y-4 text-left">
                    <div className="flex justify-between items-start gap-4 pb-3 border-b border-slate-100">
                      <div>
                        <h4 className="font-bold text-gray-900 text-sm leading-tight">{kra.name}</h4>
                        <p className="text-xs text-gray-500 mt-1">{kra.description || "No description provided."}</p>
                      </div>
                      <span className="bg-red-50 text-red-600 border border-red-100 text-[10px] font-bold px-2.5 py-0.5 rounded-lg shrink-0">
                        Weightage: {kra.weightage}%
                      </span>
                    </div>

                    {/* ✅ Upgraded to sm:grid-cols-3 to cleanly render Goal/Metric Direction */}
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 py-2 text-[11px] text-gray-500 border-b border-dashed border-gray-100 bg-slate-50/20 p-2.5 rounded-lg">
                      <div>
                        <span className="text-[9px] text-gray-400 font-bold uppercase block">Review Frequency</span>
                        <span className="font-semibold text-gray-700">{formatCycleDisplay(kra.reviewCycle)}</span>
                      </div>
                      <div>
                        <span className="text-[9px] text-primary font-bold uppercase block">Target</span>
                        <span className="font-bold text-gray-950">
                          {kra.targetValue || kra.monthlyTarget || kra.yearlyTarget || "N/A"}
                        </span>
                      </div>
                      <div>
                        <span className="text-[9px] text-gray-400 font-bold uppercase block">Goal Direction</span>
                        <span className="font-semibold text-gray-700">
                          {kra.metricType === "LOWER_IS_BETTER" ? "Lower is Better" : "Higher is Better"}
                        </span>
                      </div>
                    </div>

                    {/* Actual value and achievement percentage tracker inside Modal view */}
                    {kra.actualValue && (
                      <div className="bg-slate-50 border border-slate-100 rounded-lg p-3 text-xs space-y-1.5">
                        <span className="block font-bold text-slate-500">Actual Achievement Tracker</span>
                        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 text-[11px]">
                          <div>
                            <span className="text-[9px] text-gray-400 font-bold block uppercase">Actual Value</span>
                            <span className="font-extrabold text-emerald-600">{kra.actualValue}</span>
                          </div>
                          {kra.achievementPercentage !== undefined && kra.achievementPercentage !== null && (
                            <>
                              <div>
                                <span className="text-[9px] text-gray-400 font-bold block uppercase">Achievement %</span>
                                <div className="flex flex-wrap items-center gap-1.5 mt-0.5">
                                  <span className="font-extrabold text-primary">{kra.achievementPercentage}%</span>
                                  <span className={`text-[9px] font-bold px-1.5 py-0.2 rounded border uppercase tracking-wider ${getRatingBadgeClass(getAchievementRating(kra.achievementPercentage))}`}>
                                    {getAchievementRating(kra.achievementPercentage)}
                                  </span>
                                </div>
                              </div>
                              <div>
                                <span className="text-[9px] text-gray-400 font-bold block uppercase">Earned Score Contribution</span>
                                <span className="font-extrabold text-slate-700">{kra.weightedScore?.toFixed(2)} / {kra.weightage}.00</span>
                              </div>
                            </>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Stage-wise Ratings and Feedbacks Matrix */}
                    <div className="space-y-3 text-xs">
                      {kra.evaluations && kra.evaluations.length > 0 ? (
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                          {kra.evaluations.map((evalItem, eIdx) => {
                            // FIXED: Standardized team evaluation weighted score formula from (rating * weight) / 100 to (rating / 5) * weightage
                            const weightedContribution = (Number(evalItem.rating) / 5) * Number(kra.weightage);
                            
                            return (
                              <div key={eIdx} className="bg-slate-50/60 border rounded-lg p-3 space-y-2 flex flex-col justify-between">
                                <div className="space-y-1">
                                  <span className="block font-bold text-[10px] uppercase tracking-wider text-slate-400">
                                    {evalItem.stageName || evalItem.stageKey}
                                  </span>
                                  <p className="text-slate-600 leading-relaxed italic">
                                    "{evalItem.feedback || "No feedback provided."}"
                                  </p>
                                </div>
                                <div className="pt-2 border-t flex flex-col gap-1">
                                  <div className="flex justify-between items-center text-[10px] text-slate-400 font-semibold">
                                    <span>Weighted Score:</span>
                                    <span className="font-bold text-slate-600">
                                      ({evalItem.rating} / 5) × {kra.weightage}% = {weightedContribution.toFixed(2)}
                                    </span>
                                  </div>
                                  <div className="flex justify-between items-center">
                                    <span className="text-[10px] font-semibold text-slate-400">Rating:</span>
                                    <span className="flex items-center gap-0.5 font-bold text-amber-600 bg-amber-50 px-2 py-0.5 rounded border border-amber-100">
                                      {evalItem.rating} <Star className="w-3 h-3 fill-amber-500 text-amber-500" />
                                    </span>
                                  </div>
                                </div>
                                
                                {/* Added: Submitted By & Submitted Date block using dynamic evaluatorId populate */}
                                <div className="flex justify-between items-center text-[9px] text-slate-400 border-t border-dashed pt-1.5 mt-1 font-semibold">
                                  <span>By: <strong>{getEvaluatorName(evalItem.stageKey, evalItem.evaluatorId)}</strong></span>
                                  {evalItem.submittedAt && (
                                    <span>On: {new Date(evalItem.submittedAt).toLocaleDateString()}</span>
                                  )}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      ) : (
                        <p className="text-slate-400 text-center italic py-2">No individual evaluations found for this KRA.</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>

            </div>

            {/* Modal Footer */}
            <div className="p-4 border-t bg-slate-50 flex justify-end gap-3 shrink-0">
              {selectedReview.status !== "COMPLETED" && 
               !isSelfAssessmentStage(selectedReview) && (
                <button
                  onClick={() => handleSkipStage(selectedReview)}
                  disabled={skippingId === selectedReview._id}
                  className="px-4 py-2 bg-amber-500 hover:bg-amber-600 text-white rounded-lg text-xs font-bold uppercase transition-colors"
                >
                  {skippingId === selectedReview._id ? "Bypassing..." : "Skip Active Stage"}
                </button>
              )}
              
              {/* Start Evaluation trigger inside Review Details footer */}
              {(() => {
                const activeStage = selectedReview.stages?.[selectedReview.currentStageIndex];
                const activeEvaluatorId = activeStage?.evaluatorId?._id || activeStage?.evaluatorId;
                const isMyTurn = selectedReview.status !== "COMPLETED" && activeEvaluatorId?.toString() === currentUser?._id?.toString();
                
                return isMyTurn && (
                  <button
                    onClick={() => {
                      setSelectedReview(null);
                      navigate(`reviews/evaluation/${selectedReview._id}`); // Redirects manager to the evaluation page
                    }}
                    className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-xs font-bold uppercase transition-colors"
                  >
                    Start Evaluation
                  </button>
                );
              })()}

              <button
                onClick={() => setSelectedReview(null)}
                className="px-4 py-2 bg-white hover:bg-slate-50 text-slate-700 border rounded-lg text-xs font-bold uppercase transition-colors"
              >
                Close View
              </button>
            </div>

          </div>
        </div>
      )}
  </>
  );
}